<template>
  <div
    class="service-record-downloader"
    role="button"
    tabindex="0"
    :aria-label="`Open attachment: ${text}`"
    @click="handleAttachmentClick"
    @keydown.enter="handleAttachmentClick"
  >
    <span class="service-record-downloader__text" :class="textClasses">
      {{ text }}
    </span>
    <div class="service-record-downloader__icon">
      <i class="pi pi-download" />
    </div>
    <!-- <img
      :src="DownloadIcon"
      :alt="text"
      class="service-record-downloader__icon"
      :class="iconClasses"
    /> -->
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import useFileDownload from "@/composables/useFileDownload";
// import DownloadIcon from "/images/service-report/download.svg";

interface Props {
  text: string;
  attachmentId: string;
  supplierId: number;
  attachmentUri?: string;
  textUppercase?: boolean;
  hideForSmAndBelow?: boolean;
  download?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  textUppercase: false,
  hideForSmAndBelow: false,
  download: false,
  attachmentUri: undefined,
});

const router = useRouter();
const { viewAttachmentFile } = useFileDownload();

const textClasses = computed(() => {
  return props.textUppercase
    ? ["service-record-downloader__text--uppercase"]
    : [];
});

// const iconClasses = computed(() => {
//   return props.hideForSmAndBelow
//     ? ["service-record-downloader__icon--hide-responsive"]
//     : [];
// });

const handleAttachmentClick = async () => {
  // If attachmentUri is available, use it directly (for service records)
  if (props.attachmentUri) {
    window.open(props.attachmentUri, "_blank", "noopener,noreferrer");
    return;
  }

  // Fallback to API call if no attachmentUri (for other attachment types)
  if (!props.attachmentId) {
    console.warn(
      "ServiceRecordDownloader: No attachmentId or attachmentUri provided",
    );
    return;
  }

  try {
    await viewAttachmentFile(
      {
        attachmentId: props.attachmentId,
        supplierId: props.supplierId,
      },
      router,
    );
  } catch (error) {
    console.error("ServiceRecordDownloader: Error viewing attachment:", error);
    // TODO: Show error message to user
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.service-record-downloader {
  color: $color-primary-darker;
  font-weight: $font-weight-medium;
  font-size: $font-size-sm;
  line-height: 1.25;
  text-decoration: none;
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid $color-secondary-lighter;
  transition: all 0.2s ease;
  background-color: #fff;
  cursor: pointer;
  justify-content: space-between;
  gap: 1rem;

  &:hover {
    background-color: $color-primary;
    color: #fff;
    border-color: $color-primary;
    text-decoration: none;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: 2px solid rgba($color-primary, 0.3);
    outline-offset: 2px;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:active {
    transform: translateY(0);
  }

  &__text {
    &--uppercase {
      text-transform: uppercase;
    }
  }

  &__icon {
    margin-left: 0.5rem;
    width: 1rem;
    height: 1rem;
    transition: transform 0.2s ease;

    &--hide-responsive {
      @media only screen and (max-width: $bp-sm) {
        display: none;
      }
    }
  }

  &:hover &__icon {
    transform: scale(1.1);
  }
}
</style>
